import { TextareaHTMLAttributes, forwardRef, useEffect, useRef, useCallback } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  /** Auto-resize do zawartości (min/max rows). */
  autoResize?: boolean;
  minRows?: number;
  maxRows?: number;
}

const BASE =
  'w-full rounded-input bg-surface-base/60 text-text-primary text-sm leading-relaxed ' +
  'border transition-colors duration-fast ease-smooth ' +
  'placeholder:text-text-disabled ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 focus-visible:border-accent-primary/50 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed resize-y';

const STATE_DEFAULT = 'border-border';
const STATE_ERROR   = 'border-danger/60 focus-visible:ring-danger/30 focus-visible:border-danger';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { error, autoResize, minRows = 3, maxRows = 12, className, onChange, value, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const setRefs = useCallback((el: HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
  }, [ref]);

  const resize = useCallback(() => {
    const el = innerRef.current;
    if (!el || !autoResize) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const min = minRows * lineHeight;
    const max = maxRows * lineHeight;
    el.style.height = Math.min(Math.max(el.scrollHeight, min), max) + 'px';
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  }, [autoResize, minRows, maxRows]);

  useEffect(() => {
    resize();
  }, [resize, value]);

  const state = error ? STATE_ERROR : STATE_DEFAULT;
  const rows = autoResize ? minRows : rest.rows ?? minRows;

  return (
    <textarea
      ref={setRefs}
      rows={rows}
      value={value}
      onChange={(e) => {
        onChange?.(e);
        resize();
      }}
      className={[BASE, state, 'px-field-x py-field-y', autoResize ? 'resize-none' : '', className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});
